/**
 * Sending bytes to the API, and taking them back when nothing points at them.
 *
 * Its own file because two screens now upload: an app's screenshots and icon,
 * and a post's cover. It was written inside `apps.ts` when there was one
 * caller, and a second copy of "how do we discard an orphan" is exactly the
 * kind of duplication that lets the two drift.
 */

const FILES_PATH = '/files';

/**
 * An upload's own refusal, carried where `saveMessage` will find it.
 *
 * Shaped like a `graphql-request` failure on purpose. `saveMessage` reads the
 * server's sentence out of `response.errors[0].message` and falls back to
 * "Could not save" for anything else — so a plain `Error` from here lost the
 * one message worth showing: *"that file is not a PNG, JPEG or WebP image"*
 * says what to do next, and the fallback does not.
 */
export class UploadRefused extends Error {
  readonly response: { errors: { message: string }[] };

  constructor(message: string) {
    super(message);
    this.name = 'UploadRefused';
    this.response = { errors: [{ message }] };
  }
}

/**
 * Sends the bytes, and answers with the stored file.
 *
 * Not GraphQL: a multipart body is not something a GraphQL request carries, so
 * the API takes uploads on a route of its own. `credentials: 'include'` for the
 * same reason every query needs it — the session is an httpOnly cookie, and
 * without this the browser sends nothing and the API sees an anonymous caller.
 *
 * The path is relative so it stays same-origin, which is what makes the cookie
 * travel and what lets `<img src>` work with no configuration; the dev server
 * proxies it to the API.
 */
export const uploadFile = async (file: File): Promise<{ id: string; url: string }> => {
  const body = new FormData();
  body.append('file', file);

  const answer = await fetch(FILES_PATH, { method: 'POST', body, credentials: 'include' });
  if (!answer.ok) {
    /*
     * The server's own sentence, when it sent one. It is written for the
     * person looking at the screen — "that file is not a PNG, JPEG or WebP
     * image" says what to do next, where "Upload failed" does not.
     */
    const said = (await answer.json().catch(() => null)) as { message?: string } | null;
    throw new UploadRefused(said?.message ?? `The upload failed (${answer.status}).`);
  }
  return (await answer.json()) as { id: string; url: string };
};

/**
 * Takes an upload back when nothing ended up pointing at it.
 *
 * Best effort, and deliberately silent: the caller is already reporting why the
 * picture could not be attached, and a second failure about tidying up is noise
 * on top of it. Without this a mutation that fails after the bytes are stored
 * leaves a file nothing references and nothing sweeps — the exact waste
 * `FileService.saveOrDiscard` was written to avoid at the other end.
 */
export const discardUpload = async (fileId: string): Promise<void> => {
  try {
    await fetch(`${FILES_PATH}/${fileId}`, { method: 'DELETE', credentials: 'include' });
  } catch {
    // Nothing useful to do: the upload is already lost to the caller either
    // way, and this is cleanup rather than the operation they asked for.
  }
};
